import type { ReactNode } from 'react';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import { TextLines } from './text-lines';

interface Benefit {
    readonly title: TranslationKey;
    readonly description: TranslationKey;
    readonly icon: ReactNode;
}

interface AboutPromise {
    readonly title: TranslationKey;
    readonly text: TranslationKey;
}

/**
 * The three promises, each written on the live page as a bold lead-in and a sentence. They are two
 * keys rather than one string containing `<strong>`, so the emphasis is markup here and cannot be
 * lost or misplaced in a translation.
 */
const PROMISES: readonly AboutPromise[] = [
    { title: 'site.about.check1.title', text: 'site.about.check1.text' },
    { title: 'site.about.check2.title', text: 'site.about.check2.text' },
    { title: 'site.about.check3.title', text: 'site.about.check3.text' }
];

const BENEFITS: readonly Benefit[] = [
    {
        title: 'site.ben1.title',
        description: 'site.ben1.desc',
        icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    },
    {
        title: 'site.ben2.title',
        description: 'site.ben2.desc',
        icon: (
            <>
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </>
        )
    },
    {
        title: 'site.ben3.title',
        description: 'site.ben3.desc',
        icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    },
    {
        title: 'site.ben4.title',
        description: 'site.ben4.desc',
        icon: (
            <>
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
            </>
        )
    },
    {
        title: 'site.ben5.title',
        description: 'site.ben5.desc',
        icon: (
            <>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </>
        )
    },
    {
        title: 'site.ben6.title',
        description: 'site.ben6.desc',
        icon: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    }
];

/** Who the company is: the promises on the left, the six benefit cards on the right. */
export function AboutSection() {
    const { t } = useTranslation();

    return (
        <section className="section section-alt" id="about">
            <div className="container">
                <div className="about-grid">
                    <div className="about-text">
                        <span className="section-label">{t('site.about.label')}</span>
                        <h2 className="section-title">
                            <TextLines text={t('site.about.title')} />
                        </h2>
                        <p className="section-subtitle">{t('site.about.subtitle')}</p>
                        <div className="about-checklist">
                            {PROMISES.map((promise) => (
                                <div className="check-item" key={promise.title}>
                                    <div className="check-icon">
                                        <svg viewBox="0 0 24 24">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    </div>
                                    <p className="check-text">
                                        <strong>{t(promise.title)}</strong> — {t(promise.text)}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <a href="#contact" className="btn btn-outline-brand">
                            {t('site.about.cta')}
                        </a>
                    </div>
                    <div>
                        <div className="about-benefits">
                            {BENEFITS.map((benefit) => (
                                <div className="benefit-card" key={benefit.title}>
                                    <div className="benefit-icon">
                                        <svg viewBox="0 0 24 24">{benefit.icon}</svg>
                                    </div>
                                    <div className="benefit-title">{t(benefit.title)}</div>
                                    <div className="benefit-desc">{t(benefit.description)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
